import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from '@emotion/styled';
import {
    getPlatformEngagements,
    GroupedTopLevelMetrics
} from '../../../../../shared/api/controllers/engagementAnalyticsReporting';
import Loader from '../../../../../shared/components/general/Loader';
import { normalizeEngagementAnalyticsFilterToUtc } from '../../../../../shared/utils/engagementAnalyticsTransformers';
import {
    buildPlatformHealthIgnoredRows,
    buildPlatformHealthMetricsFromEngagements,
    buildPlatformHealthRows,
    countPlatformHealthLocationsWithVolume,
    DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS,
    getPlatformHealthLocations,
    PlatformHealthLocation,
    PlatformHealthVariant
} from '../platformHealth';
import {
    createDefaultPlatformHealthThresholdConfig,
    loadPlatformHealthThresholdConfig,
    normalizePlatformHealthThreshold,
    PlatformHealthThresholdChanges,
    PlatformHealthThresholdConfig,
    savePlatformHealthThresholdChanges
} from '../platformHealthThresholds';
import { loadPlatformHealthTarget, savePlatformHealthTarget } from '../platformHealthTarget';
import { PlatformOrganizationItemModel } from './PlatformOrganizationItem';

interface PlatformHealthDashboardProps {
    variant: PlatformHealthVariant;
    organizations: PlatformOrganizationItemModel[];
    selectedOrganizationIds: Set<string>;
    startDate: string;
    endDate: string;
    shouldLoadData: boolean;
    canEditThresholds: boolean;
    currentUserEmail: string;
    refreshKey: number;
    onLoadingChange: (isLoading: boolean) => void;
}

const PlatformHealthDashboard: React.FC<PlatformHealthDashboardProps> = ({
    variant,
    organizations,
    selectedOrganizationIds,
    startDate,
    endDate,
    shouldLoadData,
    canEditThresholds,
    currentUserEmail,
    refreshKey,
    onLoadingChange
}) => {
    const [metricsByOrganization, setMetricsByOrganization] = useState<Record<string, GroupedTopLevelMetrics>>({});
    const [failedOrganizationNames, setFailedOrganizationNames] = useState<string[]>([]);
    const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
    const [thresholdConfig, setThresholdConfig] = useState<PlatformHealthThresholdConfig>(createDefaultPlatformHealthThresholdConfig());
    const [thresholdLoadError, setThresholdLoadError] = useState('');
    const [thresholdSaveError, setThresholdSaveError] = useState('');
    const [isLoadingThresholds, setIsLoadingThresholds] = useState(true);
    const [isSavingThresholds, setIsSavingThresholds] = useState(false);
    const [defaultTarget, setDefaultTarget] = useState(() => loadPlatformHealthTarget(variant));
    const [targetDraft, setTargetDraft] = useState(String(defaultTarget));
    const [targetSaveError, setTargetSaveError] = useState('');
    const [isTargetDialogOpen, setIsTargetDialogOpen] = useState(false);
    const [minimumAttempts, setMinimumAttempts] = useState(DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS);
    const [minimumAttemptsDraft, setMinimumAttemptsDraft] = useState(String(DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS));
    const [isMinimumAttemptsDialogOpen, setIsMinimumAttemptsDialogOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState<PlatformHealthLocation | null>(null);
    const [thresholdDraft, setThresholdDraft] = useState('');
    const [removingOverrideApplicationId, setRemovingOverrideApplicationId] = useState('');
    const [overrideRemovalError, setOverrideRemovalError] = useState('');

    const allLocations = useMemo(() => getPlatformHealthLocations(organizations), [organizations]);
    const variantOverrides = thresholdConfig.overrides[variant] || {};

    useEffect(() => {
        const storedTarget = loadPlatformHealthTarget(variant);
        setDefaultTarget(storedTarget);
        setTargetDraft(String(storedTarget));
        setTargetSaveError('');
        setIsTargetDialogOpen(false);
    }, [variant]);

    useEffect(() => {
        let cancelled = false;
        setIsLoadingThresholds(true);
        setThresholdLoadError('');

        loadPlatformHealthThresholdConfig().then(result => {
            if (cancelled) return;
            if (result.resultType === 'Ok' && result.data) {
                setThresholdConfig(result.data.config);
            } else {
                setThresholdLoadError((result.errors || []).join(' ') || 'Unable to load platform health thresholds.');
            }
        }).catch(() => {
            if (!cancelled) {
                setThresholdLoadError('Unable to load platform health thresholds.');
            }
        }).finally(() => {
            if (!cancelled) setIsLoadingThresholds(false);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!shouldLoadData || !startDate || !endDate) {
            setMetricsByOrganization({});
            setFailedOrganizationNames([]);
            setIsLoadingMetrics(false);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        const load = async () => {
            setIsLoadingMetrics(true);
            setFailedOrganizationNames([]);
            const nextMetrics: Record<string, GroupedTopLevelMetrics> = {};
            const filter = normalizeEngagementAnalyticsFilterToUtc({
                startDate,
                endDate,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                signal: controller.signal
            });

            // Use the same paginated engagement source as the Analytics tab. The
            // platform summaries are grouped by organization, while Health needs
            // application-level counts for each location.
            const pageSize = 5000;
            let skip = 0;
            let hasMoreData = true;
            const engagements = [];
            while (hasMoreData && !controller.signal.aborted) {
                const result = await getPlatformEngagements(
                    organizations.map(organization => organization.id),
                    { ...filter, skip, take: pageSize }
                );
                if (result.resultType !== 'Ok') {
                    throw new Error((result.errors || []).join(' ') || 'Unable to load platform engagements.');
                }

                const pageEngagements = Object.values(result.data || {}).reduce((all, group) => {
                    all.push(...group);
                    return all;
                }, []);
                engagements.push(...pageEngagements);

                // The Analytics loader uses this check to support both paginated
                // responses and older backends that return the entire result set.
                hasMoreData = pageEngagements.length === pageSize;
                skip += pageEngagements.length;
            }

            const metricsByApplication = buildPlatformHealthMetricsFromEngagements(engagements, variant);
            organizations.forEach(organization => {
                nextMetrics[organization.id] = organization.matchingApps.reduce((organizationMetrics, application) => {
                    if (metricsByApplication[application.id]) {
                        organizationMetrics[application.id] = metricsByApplication[application.id];
                    }
                    return organizationMetrics;
                }, {} as GroupedTopLevelMetrics);
            });

            if (!cancelled) {
                setMetricsByOrganization(nextMetrics);
                setFailedOrganizationNames([]);
                setIsLoadingMetrics(false);
            }
        };

        load().catch(() => {
            if (!cancelled) {
                setMetricsByOrganization({});
                setFailedOrganizationNames(organizations.map(organization => organization.name).sort());
                setIsLoadingMetrics(false);
            }
        });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [organizations, shouldLoadData, startDate, endDate, refreshKey, variant]);

    useEffect(() => {
        onLoadingChange(isLoadingMetrics || isLoadingThresholds || isSavingThresholds);
    }, [isLoadingMetrics, isLoadingThresholds, isSavingThresholds, onLoadingChange]);

    useEffect(() => () => onLoadingChange(false), [onLoadingChange]);

    const lowPerformingRows = useMemo(() => buildPlatformHealthRows(
        organizations,
        metricsByOrganization,
        selectedOrganizationIds,
        variant,
        variantOverrides,
        defaultTarget,
        minimumAttempts
    ), [organizations, metricsByOrganization, selectedOrganizationIds, variant, variantOverrides, defaultTarget, minimumAttempts]);
    const ignoredRows = useMemo(() => buildPlatformHealthIgnoredRows(
        organizations,
        metricsByOrganization,
        selectedOrganizationIds,
        variant,
        variantOverrides,
        defaultTarget,
        minimumAttempts
    ), [organizations, metricsByOrganization, selectedOrganizationIds, variant, variantOverrides, defaultTarget, minimumAttempts]);
    const qualifyingLocationCount = useMemo(() => countPlatformHealthLocationsWithVolume(
        organizations,
        metricsByOrganization,
        selectedOrganizationIds,
        variant,
        minimumAttempts
    ), [organizations, metricsByOrganization, selectedOrganizationIds, variant, minimumAttempts]);

    const normalizedTargetDraft = normalizePlatformHealthThreshold(targetDraft);

    const openTargetDialog = () => {
        setTargetDraft(String(defaultTarget));
        setTargetSaveError('');
        setIsTargetDialogOpen(true);
    };

    const saveTarget = () => {
        if (normalizedTargetDraft == null) return;
        if (!savePlatformHealthTarget(variant, normalizedTargetDraft)) {
            setTargetSaveError('Unable to save the target in local storage.');
            return;
        }
        setDefaultTarget(normalizedTargetDraft);
        setIsTargetDialogOpen(false);
    };

    const openMinimumAttemptsDialog = () => {
        setMinimumAttemptsDraft(String(minimumAttempts));
        setIsMinimumAttemptsDialogOpen(true);
    };

    const applyMinimumAttempts = () => {
        const value = Number(minimumAttemptsDraft);
        if (!Number.isInteger(value) || value < DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS) return;
        setMinimumAttempts(value);
        setIsMinimumAttemptsDialogOpen(false);
    };

    const openThresholdDialog = (location: PlatformHealthLocation) => {
        setEditingLocation(location);
        setThresholdDraft(String(variantOverrides[location.applicationId] ?? defaultTarget));
        setThresholdSaveError('');
    };

    const closeThresholdDialog = () => {
        if (isSavingThresholds) return;
        setEditingLocation(null);
        setThresholdSaveError('');
    };

    const normalizedThresholdDraft = normalizePlatformHealthThreshold(thresholdDraft);
    const editingHasOverride = editingLocation
        ? variantOverrides[editingLocation.applicationId] != null
        : false;
    const savedEditingThreshold = editingLocation
        ? variantOverrides[editingLocation.applicationId] ?? defaultTarget
        : defaultTarget;
    const hasThresholdChange = normalizedThresholdDraft != null && (
        normalizedThresholdDraft !== savedEditingThreshold ||
        (editingHasOverride && normalizedThresholdDraft === defaultTarget)
    );
    const hasValidMinimumAttempts = Number.isInteger(Number(minimumAttemptsDraft)) &&
        Number(minimumAttemptsDraft) >= DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS;

    const saveThresholds = async () => {
        if (!editingLocation || normalizedThresholdDraft == null || !hasThresholdChange) return;
        setIsSavingThresholds(true);
        setThresholdSaveError('');
        try {
            const changes: PlatformHealthThresholdChanges = {
                [editingLocation.applicationId]: normalizedThresholdDraft === defaultTarget
                    ? null
                    : normalizedThresholdDraft
            };
            const result = await savePlatformHealthThresholdChanges(variant, changes, currentUserEmail);
            if (result.resultType !== 'Ok' || !result.data) {
                setThresholdSaveError((result.errors || []).join(' ') || 'Unable to save platform health thresholds.');
                return;
            }
            setThresholdConfig(result.data.config);
            setEditingLocation(null);
        } catch {
            setThresholdSaveError('Unable to save platform health thresholds.');
        } finally {
            setIsSavingThresholds(false);
        }
    };

    const removeLocationOverride = async (applicationId: string) => {
        if (!canEditThresholds || isSavingThresholds || thresholdLoadError) return;
        setIsSavingThresholds(true);
        setRemovingOverrideApplicationId(applicationId);
        setOverrideRemovalError('');
        try {
            const result = await savePlatformHealthThresholdChanges(
                variant,
                { [applicationId]: null },
                currentUserEmail
            );
            if (result.resultType !== 'Ok' || !result.data) {
                setOverrideRemovalError((result.errors || []).join(' ') || 'Unable to remove the location threshold override.');
                return;
            }
            setThresholdConfig(result.data.config);
        } catch {
            setOverrideRemovalError('Unable to remove the location threshold override.');
        } finally {
            setRemovingOverrideApplicationId('');
            setIsSavingThresholds(false);
        }
    };

    const metricLabel = variant === 'ordering' ? 'Order Place Rate' : 'Containment Rate';
    const numeratorLabel = variant === 'ordering' ? 'placed' : 'contained';
    const denominatorLabel = variant === 'ordering' ? 'attempts' : 'containable';
    const selectedLocationCount = allLocations.filter(location => selectedOrganizationIds.has(location.organizationId)).length;
    const dateRangeLabel = startDate && endDate
        ? `${moment(startDate).format('MMM D, YYYY')} – ${moment(endDate).format('MMM D, YYYY')}`
        : 'Choose a date range';

    return (
        <HealthPage>
            <HealthHeader>
                <div>
                    <Eyebrow>{variant === 'ordering' ? 'Platform Ordering' : 'Platform Scheduling'}</Eyebrow>
                    <h1>Location Health</h1>
                    <p>{metricLabel} for {selectedLocationCount.toLocaleString()} selected live locations · {dateRangeLabel}</p>
                </div>
                <HeaderActions>
                    <TargetButton type="button" onClick={openTargetDialog}>
                        Target: {defaultTarget.toFixed(1)}%
                    </TargetButton>
                    <MinimumAttemptsButton type="button" onClick={openMinimumAttemptsDialog}>
                        Minimum attempts: {minimumAttempts.toLocaleString()}
                    </MinimumAttemptsButton>
                </HeaderActions>
            </HealthHeader>

            {thresholdLoadError && <ErrorBanner>{thresholdLoadError} Using your local {defaultTarget.toFixed(1)}% target; per-location editing is disabled.</ErrorBanner>}
            {failedOrganizationNames.length > 0 && (
                <WarningBanner>
                    Metrics were unavailable for {failedOrganizationNames.length} organization{failedOrganizationNames.length === 1 ? '' : 's'}: {failedOrganizationNames.join(', ')}.
                </WarningBanner>
            )}

            <SummaryCard>
                <SummaryValue>{isLoadingMetrics ? '—' : lowPerformingRows.length.toLocaleString()}</SummaryValue>
                <div>
                    <strong>Low-performing locations</strong>
                    <span>Locations below their configured {metricLabel.toLowerCase()} threshold with at least {minimumAttempts.toLocaleString()} qualifying attempt{minimumAttempts === 1 ? '' : 's'}.</span>
                </div>
            </SummaryCard>

            {isLoadingMetrics ? (
                <LoadingWrapper><Loader /></LoadingWrapper>
            ) : !shouldLoadData ? (
                <EmptyState>Select a date range to load location health.</EmptyState>
            ) : qualifyingLocationCount === 0 ? (
                <EmptyState>No qualifying {variant === 'ordering' ? 'order-attempt' : 'containable-engagement'} volume was returned for the selected locations and date range.</EmptyState>
            ) : lowPerformingRows.length === 0 ? (
                <EmptyState>No selected locations are below their configured threshold for this date range.</EmptyState>
            ) : (
                <TableWrapper>
                    <HealthTable>
                        <thead>
                            <tr>
                                <th>Organization</th>
                                <th>Location</th>
                                <th>{metricLabel}</th>
                                <th>Volume</th>
                                <th>Threshold</th>
                                <th>Below by</th>
                                {canEditThresholds && <th aria-label="Threshold actions" />}
                            </tr>
                        </thead>
                        <tbody>
                            {lowPerformingRows.map(row => (
                                <tr key={row.applicationId}>
                                    <td>{row.organizationName}</td>
                                    <td><LocationLink href={`/v/apps/${row.applicationId}`}>{row.applicationName}</LocationLink></td>
                                    <td><RateValue>{row.rate.toFixed(1)}%</RateValue></td>
                                    <td>{row.numerator.toLocaleString()} {numeratorLabel} / {row.denominator.toLocaleString()} {denominatorLabel}</td>
                                    <td>{row.threshold.toFixed(1)}%</td>
                                    <td><DeficitPill>{row.deficit.toFixed(1)} pts</DeficitPill></td>
                                    {canEditThresholds && (
                                        <td>
                                            <ChangeThresholdButton
                                                type="button"
                                                onClick={() => openThresholdDialog(row)}
                                                disabled={isLoadingThresholds || Boolean(thresholdLoadError)}
                                            >
                                                Change threshold
                                            </ChangeThresholdButton>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </HealthTable>
                </TableWrapper>
            )}

            {!isLoadingMetrics && shouldLoadData && ignoredRows.length > 0 && (
                <IgnoredLocationsDetails>
                    <IgnoredLocationsSummary>
                        <span>
                            <strong>Ignored locations ({ignoredRows.length.toLocaleString()})</strong>
                            <small>Below the {defaultTarget.toFixed(1)}% target, but passing a modified location threshold.</small>
                        </span>
                        <SummaryChevron aria-hidden="true">⌄</SummaryChevron>
                    </IgnoredLocationsSummary>
                    <IgnoredLocationsBody>
                        {overrideRemovalError && <ErrorBanner>{overrideRemovalError}</ErrorBanner>}
                        <TableWrapper>
                            <HealthTable>
                                <thead>
                                    <tr>
                                        <th>Organization</th>
                                        <th>Location</th>
                                        <th>{metricLabel}</th>
                                        <th>Volume</th>
                                        <th>Modified threshold</th>
                                        <th>Target</th>
                                        <th>Below target by</th>
                                        {canEditThresholds && <th aria-label="Override actions" />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ignoredRows.map(row => (
                                        <tr key={row.applicationId}>
                                            <td>{row.organizationName}</td>
                                            <td><LocationLink href={`/v/apps/${row.applicationId}`}>{row.applicationName}</LocationLink></td>
                                            <td><RateValue>{row.rate.toFixed(1)}%</RateValue></td>
                                            <td>{row.numerator.toLocaleString()} {numeratorLabel} / {row.denominator.toLocaleString()} {denominatorLabel}</td>
                                            <td>{row.threshold.toFixed(1)}%</td>
                                            <td>{defaultTarget.toFixed(1)}%</td>
                                            <td><DeficitPill>{row.targetDeficit.toFixed(1)} pts</DeficitPill></td>
                                            {canEditThresholds && (
                                                <td>
                                                    <RemoveOverrideButton
                                                        type="button"
                                                        onClick={() => removeLocationOverride(row.applicationId)}
                                                        disabled={isSavingThresholds || isLoadingThresholds || Boolean(thresholdLoadError)}
                                                    >
                                                        {removingOverrideApplicationId === row.applicationId ? 'Removing...' : 'Remove override'}
                                                    </RemoveOverrideButton>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </HealthTable>
                        </TableWrapper>
                    </IgnoredLocationsBody>
                </IgnoredLocationsDetails>
            )}

            {isTargetDialogOpen && (
                <ModalBackdrop onClick={() => setIsTargetDialogOpen(false)}>
                    <CompactDialog role="dialog" aria-modal="true" aria-label={`Change ${metricLabel} target`} onClick={event => event.stopPropagation()}>
                        <DialogHeader>
                            <div>
                                <h2>Set target</h2>
                                <p>Set the default {metricLabel.toLowerCase()} target for this Health tab.</p>
                            </div>
                            <CloseButton type="button" onClick={() => setIsTargetDialogOpen(false)} aria-label="Close">×</CloseButton>
                        </DialogHeader>
                        <CompactDialogBody>
                            {targetSaveError && <ErrorBanner>{targetSaveError}</ErrorBanner>}
                            <FieldLabel htmlFor={`platform-health-${variant}-target`}>Target percentage</FieldLabel>
                            <ThresholdEditRow>
                                <DialogNumberInput
                                    id={`platform-health-${variant}-target`}
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={targetDraft}
                                    onChange={event => setTargetDraft(event.target.value)}
                                    autoFocus
                                />
                                <span>%</span>
                            </ThresholdEditRow>
                            {normalizedTargetDraft == null && <InlineValidation>Enter a number from 0 to 100.</InlineValidation>}
                        </CompactDialogBody>
                        <DialogFooter>
                            <span>Saved in this browser for {variant}.</span>
                            <DialogActions>
                                <CancelButton type="button" onClick={() => setIsTargetDialogOpen(false)}>Cancel</CancelButton>
                                <SaveButton
                                    type="button"
                                    onClick={saveTarget}
                                    disabled={normalizedTargetDraft == null || normalizedTargetDraft === defaultTarget}
                                >
                                    Save target
                                </SaveButton>
                            </DialogActions>
                        </DialogFooter>
                    </CompactDialog>
                </ModalBackdrop>
            )}

            {isMinimumAttemptsDialogOpen && (
                <ModalBackdrop onClick={() => setIsMinimumAttemptsDialogOpen(false)}>
                    <CompactDialog role="dialog" aria-modal="true" aria-label="Change minimum attempts" onClick={event => event.stopPropagation()}>
                        <DialogHeader>
                            <div>
                                <h2>Minimum attempts</h2>
                                <p>Only show locations with at least this much qualifying volume.</p>
                            </div>
                            <CloseButton type="button" onClick={() => setIsMinimumAttemptsDialogOpen(false)} aria-label="Close">×</CloseButton>
                        </DialogHeader>
                        <CompactDialogBody>
                            <FieldLabel htmlFor="platform-health-minimum-attempts">Minimum attempts</FieldLabel>
                            <DialogNumberInput
                                id="platform-health-minimum-attempts"
                                type="number"
                                min={DEFAULT_PLATFORM_HEALTH_MINIMUM_ATTEMPTS}
                                step="1"
                                value={minimumAttemptsDraft}
                                onChange={event => setMinimumAttemptsDraft(event.target.value)}
                                autoFocus
                            />
                            {!hasValidMinimumAttempts && <InlineValidation>Enter a whole number of 1 or greater.</InlineValidation>}
                        </CompactDialogBody>
                        <DialogFooter>
                            <span>This filter is not saved.</span>
                            <DialogActions>
                                <CancelButton type="button" onClick={() => setIsMinimumAttemptsDialogOpen(false)}>Cancel</CancelButton>
                                <SaveButton
                                    type="button"
                                    onClick={applyMinimumAttempts}
                                    disabled={!hasValidMinimumAttempts}
                                >
                                    Apply
                                </SaveButton>
                            </DialogActions>
                        </DialogFooter>
                    </CompactDialog>
                </ModalBackdrop>
            )}

            {editingLocation && (
                <ModalBackdrop onClick={closeThresholdDialog}>
                    <CompactDialog role="dialog" aria-modal="true" aria-label={`Change ${editingLocation.applicationName} threshold`} onClick={event => event.stopPropagation()}>
                        <DialogHeader>
                            <div>
                                <h2>Change threshold</h2>
                                <p>{editingLocation.applicationName} · {editingLocation.organizationName}</p>
                            </div>
                            <CloseButton type="button" onClick={closeThresholdDialog} disabled={isSavingThresholds} aria-label="Close">×</CloseButton>
                        </DialogHeader>
                        <CompactDialogBody>
                            {thresholdSaveError && <ErrorBanner>{thresholdSaveError}</ErrorBanner>}
                            <FieldLabel htmlFor="platform-health-location-threshold">{metricLabel} threshold</FieldLabel>
                            <ThresholdEditRow>
                                <DialogNumberInput
                                    id="platform-health-location-threshold"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={thresholdDraft}
                                    onChange={event => setThresholdDraft(event.target.value)}
                                    disabled={isSavingThresholds}
                                    autoFocus
                                />
                                <span>%</span>
                                <ResetButton
                                    type="button"
                                    onClick={() => setThresholdDraft(String(defaultTarget))}
                                    disabled={isSavingThresholds || (!editingHasOverride && thresholdDraft === String(defaultTarget))}
                                >
                                    Reset to {defaultTarget.toFixed(1)}%
                                </ResetButton>
                            </ThresholdEditRow>
                            {normalizedThresholdDraft == null && <InlineValidation>Enter a number from 0 to 100.</InlineValidation>}
                        </CompactDialogBody>
                        <DialogFooter>
                            <span>Saved for this location.</span>
                            <DialogActions>
                                <CancelButton type="button" onClick={closeThresholdDialog} disabled={isSavingThresholds}>Cancel</CancelButton>
                                <SaveButton
                                    type="button"
                                    onClick={saveThresholds}
                                    disabled={isSavingThresholds || normalizedThresholdDraft == null || !hasThresholdChange}
                                >
                                    {isSavingThresholds ? 'Saving...' : 'Save threshold'}
                                </SaveButton>
                            </DialogActions>
                        </DialogFooter>
                    </CompactDialog>
                </ModalBackdrop>
            )}
        </HealthPage>
    );
};

const HealthPage = styled.div`
    width: 100%;
    height: 100%;
    overflow: auto;
    padding: 28px;
    background: #f5f8fa;
    color: #263747;
    font-family: Muli, sans-serif;
`;

const HealthHeader = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 20px;

    h1 { margin: 3px 0 5px; font-size: 28px; }
    p { margin: 0; color: #647486; font-size: 13px; }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const Eyebrow = styled.div`
    color: #1c6697;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const ManageButton = styled.button`
    padding: 10px 15px;
    border: 0;
    border-radius: 7px;
    background: #1c6697;
    color: white;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    &:disabled { cursor: not-allowed; opacity: 0.55; }
`;

const MinimumAttemptsButton = styled(ManageButton)`
    white-space: nowrap;
`;

const TargetButton = styled(ManageButton)`
    white-space: nowrap;
`;

const ChangeThresholdButton = styled.button`
    padding: 6px 9px;
    border: 1px solid #9eb7c8;
    border-radius: 6px;
    background: white;
    color: #1c6697;
    font: inherit;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    &:disabled { cursor: not-allowed; opacity: 0.55; }
`;

const RemoveOverrideButton = styled(ChangeThresholdButton)`
    border-color: #d5a6a6;
    color: #963c3c;
`;

const ErrorBanner = styled.div`
    margin: 0 0 14px;
    padding: 11px 13px;
    border: 1px solid #e3a8a8;
    border-radius: 7px;
    background: #fff3f3;
    color: #8b2e2e;
    font-size: 12px;
`;

const WarningBanner = styled(ErrorBanner)`
    border-color: #e5ca8c;
    background: #fff9e9;
    color: #795c18;
`;

const SummaryCard = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 18px;
    padding: 17px 20px;
    border: 1px solid #dbe4ea;
    border-radius: 10px;
    background: white;
    box-shadow: 0 3px 10px rgba(38, 55, 71, 0.05);

    div:last-child { display: flex; flex-direction: column; gap: 3px; }
    strong { font-size: 14px; }
    span { color: #647486; font-size: 12px; }
`;

const SummaryValue = styled.div`
    min-width: 52px;
    color: #a23d3d;
    font-size: 30px;
    font-weight: 800;
    text-align: center;
`;

const LoadingWrapper = styled.div`
    min-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const EmptyState = styled.div`
    padding: 50px 24px;
    border: 1px dashed #c7d3db;
    border-radius: 10px;
    background: white;
    color: #647486;
    text-align: center;
    font-size: 14px;
`;

const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
    border: 1px solid #dbe4ea;
    border-radius: 10px;
    background: white;
`;

const HealthTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th { padding: 11px 14px; background: #f0f5f8; color: #52657a; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
    td { padding: 13px 14px; border-top: 1px solid #e7edf1; vertical-align: middle; }
    tbody tr:hover { background: #f8fbfd; }
`;

const LocationLink = styled.a`
    color: #1c6697;
    font-weight: 700;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`;

const RateValue = styled.strong`
    color: #a23d3d;
    font-size: 14px;
`;

const DeficitPill = styled.span`
    display: inline-flex;
    padding: 4px 8px;
    border-radius: 999px;
    background: #fbeaea;
    color: #9c3535;
    font-weight: 700;
`;

const IgnoredLocationsDetails = styled.details`
    margin-top: 18px;
    border: 1px solid #dbe4ea;
    border-radius: 10px;
    background: white;
    overflow: hidden;

    &[open] summary {
        border-bottom: 1px solid #dbe4ea;
    }

    &[open] summary > span:last-child {
        transform: rotate(180deg);
    }
`;

const IgnoredLocationsSummary = styled.summary`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 15px 18px;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker { display: none; }
    > span:first-child { display: flex; flex-direction: column; gap: 3px; }
    strong { color: #52657a; font-size: 13px; }
    small { color: #738394; font-size: 11px; }
    &:hover { background: #f8fbfd; }
`;

const SummaryChevron = styled.span`
    color: #52657a;
    font-size: 20px;
    line-height: 1;
    transition: transform 0.15s ease;
`;

const IgnoredLocationsBody = styled.div`
    padding: 14px;

    ${TableWrapper} { border-radius: 7px; }
`;

const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1600;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(34, 46, 61, 0.58);
`;

const CompactDialog = styled.section`
    width: min(480px, 100%);
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 14px;
    background: white;
    box-shadow: 0 24px 65px rgba(21, 34, 45, 0.28);
`;

const DialogHeader = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 22px 15px;
    border-bottom: 1px solid #e1e8ed;

    h2 { margin: 0; font-size: 21px; }
    p { margin: 5px 0 0; color: #647486; font-size: 12px; }
`;

const CloseButton = styled.button`
    border: 0;
    background: transparent;
    color: #52657a;
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
`;

const CompactDialogBody = styled.div`
    padding: 20px 22px 24px;
`;

const FieldLabel = styled.label`
    display: block;
    margin-bottom: 7px;
    color: #52657a;
    font-size: 12px;
    font-weight: 700;
`;

const DialogNumberInput = styled.input`
    width: 150px;
    padding: 10px 11px;
    border: 1px solid #c8d4dc;
    border-radius: 7px;
    font: inherit;
    font-size: 13px;
    outline: none;
    &:focus { border-color: #1c6697; box-shadow: 0 0 0 2px rgba(28, 102, 151, 0.12); }
`;

const ThresholdEditRow = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    color: #647486;
    font-size: 12px;
`;

const InlineValidation = styled.div`
    margin-top: 8px;
    color: #8b2e2e;
    font-size: 11px;
`;

const ResetButton = styled.button`
    border: 0;
    background: transparent;
    color: #1c6697;
    font: inherit;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    &:disabled { color: #a8b3bc; cursor: default; }
`;

const DialogFooter = styled.footer`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 13px 22px;
    border-top: 1px solid #e1e8ed;
    background: #f7f9fb;
    color: #647486;
    font-size: 11px;
`;

const DialogActions = styled.div`
    display: flex;
    gap: 9px;
`;

const CancelButton = styled.button`
    padding: 9px 13px;
    border: 1px solid #c8d4dc;
    border-radius: 6px;
    background: white;
    color: #52657a;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

const SaveButton = styled(ManageButton)`
    padding: 9px 14px;
`;

export default PlatformHealthDashboard;
